import axios from 'axios';

// Configuration de base d'Axios avec gestion d'erreurs
// En prod, l'API est servie sur le même domaine via /api.
// Garder la possibilité d'override via REACT_APP_API_URL (utile en dev/staging).
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 10000, // 10 secondes de timeout
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    // Compat temporaire: si un token legacy est encore présent, l'envoyer en header.
    // La méthode recommandée est le cookie HttpOnly (avecCredentials).
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = (error.config?.url || '').toString();
      const isAuthLoginEndpoint = url.includes('/auth/login');
      const isAuthMeEndpoint = url.includes('/auth/me');
      if (isAuthLoginEndpoint || isAuthMeEndpoint) {
        return Promise.reject(error);
      }
      // Token expiré ou invalide
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('impersonation');
      localStorage.removeItem('isLoggedIn');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Services d'authentification
export const authService = {
  async login(email, password) {
    const response = await api.post('/auth/login', { email, password });
    // Token désormais géré via cookie HttpOnly; conserver uniquement les infos utilisateur.
    if (response.data.user) localStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  },

  async verifyTwoFactorLogin(twoFactorToken, code) {
    const response = await api.post('/auth/login/2fa', { twoFactorToken, code });
    if (response.data.user) localStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  },

  async register(userData) {
    const response = await api.post('/auth/register', userData);
    if (response.data.user) localStorage.setItem('user', JSON.stringify(response.data.user));
    return response.data;
  },

  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async getReminderSettings() {
    const response = await api.get('/auth/reminders');
    return response.data;
  },

  async updateReminderSettings(remindersEnabled) {
    const response = await api.put('/auth/reminders', { remindersEnabled });
    return response.data;
  },

  async getTwoFactorStatus() {
    const response = await api.get('/auth/2fa/status');
    return response.data;
  },

  async setupTwoFactor() {
    const response = await api.post('/auth/2fa/setup');
    return response.data;
  },

  async enableTwoFactor(code) {
    const response = await api.post('/auth/2fa/enable', { code });
    return response.data;
  },

  async disableTwoFactor(password) {
    const response = await api.post('/auth/2fa/disable', { password });
    return response.data;
  },

  logout() {
    // Best-effort côté serveur pour supprimer le cookie
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('impersonation');
    localStorage.removeItem('isLoggedIn');
    window.location.href = '/login';
  },

  async logoutRequest() {
    // Variante sans redirection automatique (utile pour gérer l'état React proprement)
    const response = await api.post('/auth/logout');
    return response.data;
  },

  async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  async verifyResetCode(email, code) {
    const response = await api.post('/auth/verify-reset-code', { email, code });
    return response.data;
  },

  async resetPassword(email, code, newPassword) {
    const response = await api.post('/auth/reset-password', { email, code, newPassword });
    return response.data;
  },

  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  getCurrentUserFromStorage() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  async adminListUsers(search = '') {
    const response = await api.get('/auth/admin/users', { params: search ? { search } : {} });
    return response.data.users || [];
  },

  async adminImpersonate(userId) {
    const response = await api.post('/auth/admin/impersonate', { userId });
    if (response.data.token) {
      const currentToken = localStorage.getItem('token');
      const currentUser = localStorage.getItem('user');
      if (!localStorage.getItem('adminToken')) {
        if (currentToken) localStorage.setItem('adminToken', currentToken);
        if (currentUser) localStorage.setItem('adminUser', currentUser);
      }
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('impersonation', JSON.stringify({
        impersonatedBy: response.data.impersonatedBy,
        startedAt: new Date().toISOString()
      }));
    }
    return response.data;
  },

  stopImpersonation() {
    const adminToken = localStorage.getItem('adminToken');
    const adminUser = localStorage.getItem('adminUser');
    if (!adminToken || !adminUser) {
      return false;
    }
    localStorage.setItem('token', adminToken);
    localStorage.setItem('user', adminUser);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('impersonation');
    return true;
  },

  getImpersonation() {
    const data = localStorage.getItem('impersonation');
    return data ? JSON.parse(data) : null;
  },

  async getTeamMembers() {
    const response = await api.get('/auth/team');
    return response.data.members || [];
  },

  async createTeamMember(payload) {
    const response = await api.post('/auth/team', payload);
    return response.data;
  },

  async updateTeamMember(id, payload) {
    const response = await api.put(`/auth/team/${id}`, payload);
    return response.data;
  },

  async deleteTeamMember(id) {
    const response = await api.delete(`/auth/team/${id}`);
    return response.data;
  },
};

// Services abonnement (Stripe)
export const subscriptionService = {
  async getStatus() {
    const response = await api.get('/subscription/status');
    return response.data;
  },

  async syncCheckoutSession(sessionId) {
    const response = await api.post('/subscription/sync-checkout-session', { sessionId });
    return response.data;
  },

  async syncLatest() {
    const response = await api.post('/subscription/sync-latest', {});
    return response.data;
  },

  async getUsage() {
    const response = await api.get('/subscription/usage');
    return response.data;
  },

  async createCheckoutSession(plan = 'pro') {
    const response = await api.post('/subscription/create-checkout-session', { plan });
    return response.data;
  },

  async cancel() {
    const response = await api.post('/subscription/cancel', {});
    return response.data;
  },

  async createPortalSession() {
    const response = await api.post('/subscription/create-portal-session', {});
    return response.data;
  },
};

// Analytics (vues / stats globales)
export const analyticsService = {
  async trackPageView(path, { sessionId, referrer } = {}) {
    const response = await api.post('/analytics/pageview', { path, sessionId, referrer });
    return response.data;
  },

  async getAdminOverview(days = 30) {
    const response = await api.get('/analytics/admin/overview', { params: { days } });
    return response.data;
  },

  async getAdminOnboardingActivities(days = 30) {
    const response = await api.get('/analytics/admin/onboarding/activity', { params: { days } });
    return response.data;
  },
};

// Services des entreprises
export const companiesService = {
  async getAll(params = {}) {
    const response = await api.get('/companies', { params });
    return response.data.companies || response.data;
  },

  async getById(id) {
    const response = await api.get(`/companies/${id}`);
    return response.data;
  },

  async create(companyData) {
    const response = await api.post('/companies', companyData);
    return response.data;
  },

  async update(id, companyData) {
    const response = await api.put(`/companies/${id}`, companyData);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/companies/${id}`);
    return response.data;
  },
};

// Services des devis
export const quotesService = {
  async getAll(params = {}) {
    const response = await api.get('/quotes', { params });
    return response.data.quotes || response.data;
  },

  async getById(id) {
    const response = await api.get(`/quotes/${id}`);
    return response.data;
  },

  async create(quoteData) {
    const response = await api.post('/quotes', quoteData);
    return response.data;
  },

  async update(id, quoteData) {
    const response = await api.put(`/quotes/${id}`, quoteData);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/quotes/${id}`);
    return response.data;
  },

  async downloadPdf(id) {
    const response = await api.get(`/quotes/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  async sendEmail(id, recipientEmail) {
    const response = await api.post(`/quotes/${id}/send-email`, { recipientEmail });
    return response.data;
  },

  async signQuote(id, { name, email }) {
    const response = await api.post(`/quotes/${id}/sign`, { name, email });
    return response.data;
  },
};

// Services des factures
export const invoicesService = {
  async getAll(params = {}) {
    const response = await api.get('/invoices', { params });
    return response.data.invoices || response.data;
  },

  async getById(id) {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  },

  async create(invoiceData) {
    const response = await api.post('/invoices', invoiceData);
    return response.data;
  },

  async update(id, invoiceData) {
    const response = await api.put(`/invoices/${id}`, invoiceData);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/invoices/${id}`);
    return response.data;
  },

  async exportCsv(id) {
    const response = await api.get(`/invoices/${id}/export/csv`, { responseType: 'blob' });
    return response.data;
  },

  async exportExcel(id) {
    const response = await api.get(`/invoices/${id}/export/excel`, { responseType: 'blob' });
    return response.data;
  },

  async signInvoice(id, payload) {
    const response = await api.post(`/invoices/${id}/sign`, payload);
    return response.data;
  },

  async createFromQuote(quoteId, invoiceData) {
    const response = await api.post(`/invoices/from-quote/${quoteId}`, invoiceData);
    return response.data;
  },

  async downloadPdf(id) {
    const response = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  async sendEmail(id, recipientEmail) {
    const response = await api.post(`/invoices/${id}/send-email`, { recipientEmail });
    return response.data;
  },
};

// Services du tableau de bord
export const dashboardService = {
  async getStats(period = '30') {
    const response = await api.get('/dashboard/stats', { params: { period } });
    return response.data;
  },

  async getMonthlyRevenue() {
    const response = await api.get('/dashboard/monthly-revenue');
    return response.data;
  },

  async getOverdueInvoices() {
    const response = await api.get('/dashboard/overdue-invoices');
    return response.data;
  },
};

// Services de comptabilité (factures fournisseurs)
export const externalDocumentsService = {
  async getAll(params = {}) {
    const response = await api.get('/external-documents', { params });
    return response.data.documents || response.data;
  },

  async getById(id) {
    const response = await api.get(`/external-documents/${id}`);
    return response.data;
  },

  async create(documentData) {
    const response = await api.post('/external-documents', documentData);
    return response.data;
  },

  async upload(formData) {
    const response = await api.post('/external-documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async update(id, documentData) {
    const response = await api.put(`/external-documents/${id}`, documentData);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/external-documents/${id}`);
    return response.data;
  },

  async download(id) {
    const response = await api.get(`/external-documents/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getStats() {
    const response = await api.get('/external-documents/stats/summary');
    return response.data;
  },
};

// Services de rapprochement bancaire
export const bankTransactionsService = {
  async getAll(params = {}) {
    const response = await api.get('/bank-transactions', { params });
    return response.data.transactions || response.data;
  },

  async importTransactions(transactions = []) {
    const response = await api.post('/bank-transactions/import', { transactions });
    return response.data;
  },

  async matchTransaction(id, invoiceId) {
    const response = await api.put(`/bank-transactions/${id}/match`, { invoiceId });
    return response.data;
  },
};

export const expenseNotesService = {
  async getAll(params = {}) {
    const response = await api.get('/expense-notes', { params });
    return response.data;
  },

  async getStats(params = {}) {
    const response = await api.get('/expense-notes/stats', { params });
    return response.data;
  },

  async create(payload) {
    const response = await api.post('/expense-notes', payload);
    return response.data;
  },

  async update(id, payload) {
    const response = await api.put(`/expense-notes/${id}`, payload);
    return response.data;
  },

  async getAudit(id) {
    const response = await api.get(`/expense-notes/${id}/audit`);
    return response.data;
  },

  async delete(id) {
    const response = await api.delete(`/expense-notes/${id}`);
    return response.data;
  },

  async exportCsv(params = {}) {
    const response = await api.get('/expense-notes/export/csv', { params, responseType: 'blob' });
    return response.data;
  },

  async exportExcel(params = {}) {
    const response = await api.get('/expense-notes/export/excel', { params, responseType: 'blob' });
    return response.data;
  }
};

export default api;